import { useState, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box, Button, Card, Typography, IconButton, CircularProgress, Chip, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, alpha, Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import PaidIcon from '@mui/icons-material/Paid';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HistoryIcon from '@mui/icons-material/History';
import axiosInstance from '../../lib/axios';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import { Invoice, InvoiceStatus, MarkInvoicePaidDto } from '../../types/b2b';

const fetchInvoice = async (id: string): Promise<Invoice> => {
  const response = await axiosInstance.get(`/invoices/${id}`);
  return response.data;
};

const getStatusColor = (status: InvoiceStatus): string => {
  switch (status) {
    case 'draft': return palette.textMuted;
    case 'sent': return palette.info;
    case 'paid': return palette.success;
    case 'partially_paid': return palette.warning;
    case 'overdue': return palette.error;
    case 'canceled': return palette.textMuted;
    case 'refunded': return palette.info;
    default: return palette.textMuted;
  }
};

export const InvoiceShow = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(searchParams.get('action') === 'pay');
  const { control, handleSubmit, reset: resetPaymentForm } = useForm<MarkInvoicePaidDto>({
    defaultValues: { amount: 0, paymentMethod: 'bank_transfer', reference: '' },
  });

  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => fetchInvoice(id!),
    enabled: !!id,
  });

  const sendMutation = useMutation({
    mutationFn: () => axiosInstance.post(`/invoices/${id}/send`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['invoice', id] }); queryClient.invalidateQueries({ queryKey: ['invoices'] }); showMessage('Invoice sent successfully', 'success'); },
    onError: (err: any) => showMessage(err.response?.data?.message || 'Failed to send invoice', 'error'),
  });

  const recordPaymentMutation = useMutation({
    mutationFn: (data: MarkInvoicePaidDto) => axiosInstance.post(`/invoices/${id}/pay`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      showMessage('Payment recorded successfully', 'success');
      setPaymentDialogOpen(false);
      resetPaymentForm();
    },
    onError: (err: any) => showMessage(err.response?.data?.message || 'Failed to record payment', 'error'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => axiosInstance.post(`/invoices/${id}/cancel`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['invoice', id] }); queryClient.invalidateQueries({ queryKey: ['invoices'] }); showMessage('Invoice canceled', 'success'); },
    onError: (err: any) => showMessage(err.response?.data?.message || 'Failed to cancel invoice', 'error'),
  });

  const handleDownloadPdf = async () => {
    try {
      const response = await axiosInstance.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice?.invoiceNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      showMessage('PDF downloaded successfully', 'success');
    } catch { showMessage('Failed to download PDF', 'error'); }
  };

  const onPaymentSubmit = (data: MarkInvoicePaidDto) => {
    if (!data.amount || data.amount <= 0) {
      showMessage('Please enter a valid amount', 'error');
      return;
    }
    recordPaymentMutation.mutate(data);
  };

  const colors = mode === 'dark'
    ? { surface: palette.surface, surfaceElevated: palette.surfaceElevated, border: palette.border, borderLight: palette.borderLight, textPrimary: palette.textPrimary, textMuted: palette.textMuted }
    : { surface: palette.light.surface, surfaceElevated: palette.light.surfaceElevated, border: palette.light.border, borderLight: palette.light.borderLight, textPrimary: palette.light.textPrimary, textMuted: palette.light.textMuted };

  const formatCurrency = (amount: number) => new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD' }).format(amount);
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('fr-DZ', { day: '2-digit', month: 'long', year: 'numeric' });
  const textFieldSx = { '& .MuiOutlinedInput-root': { backgroundColor: colors.surfaceElevated, border: `1px solid ${colors.border}`, borderRadius: theme.radius.sm, '& fieldset': { border: 'none' } }, '& .MuiInputBase-input': { color: colors.textPrimary }, '& .MuiInputLabel-root': { color: colors.textMuted } };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress sx={{ color: palette.primary }} /></Box>;
  }

  if (error || !invoice) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="h5" sx={{ color: colors.textPrimary }}>Invoice not found</Typography></Box>;
  }

  const statusColor = getStatusColor(invoice.status);
  const canEdit = invoice.status === 'draft';
  const canSend = invoice.status === 'draft';
  const canRecordPayment = ['sent', 'partially_paid', 'overdue'].includes(invoice.status);
  const canCancel = ['draft', 'sent'].includes(invoice.status);

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate('/app/invoices')} sx={{ border: `1px solid ${colors.border}`, borderRadius: theme.radius.sm, color: colors.textPrimary, '&:hover': { borderColor: palette.primary, backgroundColor: alpha(palette.primary, 0.1) } }}><ArrowBackIcon /></IconButton>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon sx={{ color: palette.primary, fontSize: 20 }} />
              <Typography variant="overline" sx={{ color: palette.primary, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.bold }}>INVOICE DETAILS</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h3" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.extrabold, color: colors.textPrimary, letterSpacing: '-0.02em' }}>{invoice.invoiceNumber}</Typography>
              <Chip label={invoice.status.replace('_', ' ').toUpperCase()} sx={{ backgroundColor: alpha(statusColor, 0.1), color: statusColor, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, border: `1px solid ${statusColor}` }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canEdit && <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/app/invoices/edit/${id}`)} sx={{ borderColor: palette.info, color: palette.info }}>Edit</Button>}
            {canSend && <Button variant="contained" startIcon={<SendIcon />} onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending} sx={{ backgroundColor: palette.info }}>Send</Button>}
            {canRecordPayment && <Button variant="contained" startIcon={<PaidIcon />} onClick={() => setPaymentDialogOpen(true)} sx={{ backgroundColor: palette.success }}>Record Payment</Button>}
            <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleDownloadPdf} sx={{ borderColor: palette.error, color: palette.error }}>PDF</Button>
            {canCancel && <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => { if (window.confirm('Cancel this invoice?')) cancelMutation.mutate(); }} sx={{ borderColor: palette.error, color: palette.error }}>Cancel</Button>}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Left Column - Main Content */}
        <Box>
          {/* Summary Card */}
          <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, mb: 3, ...animations.slideUp(0.1) }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PersonIcon sx={{ fontSize: 18, color: palette.info }} />
                  <Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>CUSTOMER</Typography>
                </Box>
                <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>{invoice.customer ? `${invoice.customer.firstName} ${invoice.customer.lastName}` : 'N/A'}</Typography>
                {invoice.customer?.email && <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>{invoice.customer.email}</Typography>}
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CalendarTodayIcon sx={{ fontSize: 18, color: palette.warning }} />
                  <Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>DUE DATE</Typography>
                </Box>
                <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: invoice.status === 'overdue' ? palette.error : colors.textPrimary }}>{formatDate(invoice.dueDate)}</Typography>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AccountBalanceIcon sx={{ fontSize: 18, color: palette.success }} />
                  <Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>PAYMENT TERMS</Typography>
                </Box>
                <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>{invoice.paymentTerms?.replace('_', ' ').toUpperCase() || 'N/A'}</Typography>
              </Box>
            </Box>
          </Card>

          {/* Line Items */}
          <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, overflow: 'hidden', mb: 3, ...animations.slideUp(0.15) }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${colors.border}` }}>
              <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>LINE ITEMS</Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.surfaceElevated }}>
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>PRODUCT</TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>QTY</TableCell>
                    <TableCell align="right" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>UNIT PRICE</TableCell>
                    <TableCell align="right" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>SUBTOTAL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.semibold, color: colors.textPrimary }}>{item.productName || 'Unknown Product'}</Typography>
                        {item.productSku && <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.xs }}>{item.productSku}</Typography>}
                      </TableCell>
                      <TableCell align="center"><Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>{item.quantity}</Typography></TableCell>
                      <TableCell align="right"><Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>{formatCurrency(item.unitPrice)}</Typography></TableCell>
                      <TableCell align="right"><Typography sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>{formatCurrency(item.subtotal)}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ p: 3, backgroundColor: colors.surfaceElevated, borderTop: `2px solid ${colors.border}` }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: 250 }}>
                  <Typography sx={{ color: colors.textMuted }}>Subtotal:</Typography>
                  <Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>{formatCurrency(invoice.subtotal || 0)}</Typography>
                </Box>
                {invoice.discount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: 250 }}>
                    <Typography sx={{ color: colors.textMuted }}>Discount:</Typography>
                    <Typography sx={{ fontFamily: typography.fontFamily.mono, color: palette.success }}>-{formatCurrency(invoice.discount)}</Typography>
                  </Box>
                )}
                {invoice.tax > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: 250 }}>
                    <Typography sx={{ color: colors.textMuted }}>Tax:</Typography>
                    <Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>{formatCurrency(invoice.tax)}</Typography>
                  </Box>
                )}
                <Divider sx={{ width: 250, borderColor: colors.border }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: 250 }}>
                  <Typography sx={{ fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>Total:</Typography>
                  <Typography sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.primary, fontSize: typography.fontSize.lg }}>{formatCurrency(invoice.total)}</Typography>
                </Box>
                {invoice.amountPaid > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: 250 }}>
                    <Typography sx={{ color: palette.success }}>Paid:</Typography>
                    <Typography sx={{ fontFamily: typography.fontFamily.mono, color: palette.success }}>-{formatCurrency(invoice.amountPaid)}</Typography>
                  </Box>
                )}
                {invoice.amountDue > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: 250, p: 1, backgroundColor: alpha(palette.warning, 0.1), borderRadius: theme.radius.xs }}>
                    <Typography sx={{ fontWeight: typography.fontWeight.bold, color: palette.warning }}>Amount Due:</Typography>
                    <Typography sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.warning }}>{formatCurrency(invoice.amountDue)}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>

          {/* Notes */}
          {invoice.notes && (
            <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, ...animations.slideUp(0.2) }}>
              <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, mb: 2 }}>NOTES</Typography>
              <Typography sx={{ color: colors.textMuted, whiteSpace: 'pre-wrap' }}>{invoice.notes}</Typography>
            </Card>
          )}
        </Box>

        {/* Right Column - History & Info */}
        <Box>
          {/* Payment History */}
          {invoice.payments && invoice.payments.length > 0 && (
            <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, mb: 3, ...animations.slideUp(0.1) }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PaidIcon sx={{ color: palette.success }} />
                <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>PAYMENTS</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {invoice.payments.map((payment, index) => (
                  <Box key={index} sx={{ p: 2, backgroundColor: colors.surfaceElevated, borderRadius: theme.radius.sm, border: `1px solid ${colors.border}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.bold, color: palette.success }}>{formatCurrency(payment.amount)}</Typography>
                      <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.xs }}>{formatDate(payment.createdAt)}</Typography>
                    </Box>
                    <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>{payment.paymentMethod?.replace('_', ' ') || 'N/A'}</Typography>
                    {payment.reference && <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.xs }}>Ref: {payment.reference}</Typography>}
                  </Box>
                ))}
              </Box>
            </Card>
          )}

          {/* History */}
          <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, ...animations.slideUp(0.15) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HistoryIcon sx={{ color: palette.info }} />
              <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>HISTORY</Typography>
            </Box>
            <Box sx={{ position: 'relative', pl: 3 }}>
              <Box sx={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, backgroundColor: colors.border }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ position: 'absolute', left: -19, width: 12, height: 12, borderRadius: '50%', backgroundColor: palette.primary, border: `2px solid ${colors.surface}` }} />
                  <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.xs }}>{formatDate(invoice.createdAt)}</Typography>
                  <Typography sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.semibold, color: colors.textPrimary }}>Invoice Created</Typography>
                </Box>
                {invoice.sentAt && (
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'absolute', left: -19, width: 12, height: 12, borderRadius: '50%', backgroundColor: palette.info, border: `2px solid ${colors.surface}` }} />
                    <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.xs }}>{formatDate(invoice.sentAt)}</Typography>
                    <Typography sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.semibold, color: colors.textPrimary }}>Invoice Sent</Typography>
                  </Box>
                )}
                {invoice.status === 'paid' && (
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'absolute', left: -19, width: 12, height: 12, borderRadius: '50%', backgroundColor: palette.success, border: `2px solid ${colors.surface}` }} />
                    <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.xs }}>{formatDate(invoice.updatedAt)}</Typography>
                    <Typography sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.semibold, color: palette.success }}>Paid in Full</Typography>
                  </Box>
                )}
                {invoice.status === 'canceled' && (
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'absolute', left: -19, width: 12, height: 12, borderRadius: '50%', backgroundColor: palette.error, border: `2px solid ${colors.surface}` }} />
                    <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.xs }}>{formatDate(invoice.updatedAt)}</Typography>
                    <Typography sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.semibold, color: palette.error }}>Invoice Canceled</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: colors.surface, border: `2px solid ${colors.border}` } }}>
        <form onSubmit={handleSubmit(onPaymentSubmit)}>
          <DialogTitle sx={{ color: colors.textPrimary, fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold }}>Record Payment</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <Box sx={{ p: 2, backgroundColor: colors.surfaceElevated, borderRadius: theme.radius.sm }}>
                <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>Amount Due</Typography>
                <Typography sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.warning, fontSize: typography.fontSize.xl }}>{formatCurrency(invoice.amountDue || 0)}</Typography>
              </Box>
              <Controller name="amount" control={control} rules={{ required: 'Amount is required', min: { value: 0.01, message: 'Amount must be positive' } }} render={({ field, fieldState }) => <TextField {...field} label="Payment Amount" type="number" error={!!fieldState.error} helperText={fieldState.error?.message} InputProps={{ startAdornment: <InputAdornment position="start">DZD</InputAdornment> }} onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)} sx={textFieldSx} />} />
              <Controller name="paymentMethod" control={control} render={({ field }) => (
                <TextField {...field} select label="Payment Method" sx={textFieldSx}>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="other">Other</option>
                </TextField>
              )} />
              <Controller name="reference" control={control} render={({ field }) => <TextField {...field} label="Reference (Optional)" placeholder="e.g., Check number, transaction ID" sx={textFieldSx} />} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setPaymentDialogOpen(false)} sx={{ color: colors.textMuted }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={recordPaymentMutation.isPending} sx={{ backgroundColor: palette.success }}>{recordPaymentMutation.isPending ? 'Recording...' : 'Record Payment'}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};
